import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { cannedResponseService } from "@/services/cannedResponseService";
import {
  CannedResponseFilters,
  CreateCannedResponseDto,
  UpdateCannedResponseDto,
} from "@/services/interfaces/ICannedResponseService";
import { ReorderDto } from "@/services/interfaces/ICategoryService";

export const cannedResponseKeys = {
  all: ["canned-responses"] as const,
  lists: () => [...cannedResponseKeys.all, "list"] as const,
  list: (filters?: CannedResponseFilters) =>
    [...cannedResponseKeys.lists(), filters] as const,
};

export const useCannedResponses = (filters: CannedResponseFilters) => {
  return useQuery({
    queryKey: cannedResponseKeys.list(filters),
    queryFn: () => cannedResponseService.getAll(filters),
    enabled: !!filters?.categoryId,
  });
};

export const useCreateCannedResponse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCannedResponseDto) =>
      cannedResponseService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cannedResponseKeys.lists() });
    },
  });
};

export const useUpdateCannedResponse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCannedResponseDto }) =>
      cannedResponseService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cannedResponseKeys.lists() });
    },
  });
};

export const useDeleteCannedResponse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => cannedResponseService.deleteCannedResponse(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cannedResponseKeys.lists() });
    },
  });
};

export const useReorderCannedResponses = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ReorderDto) => cannedResponseService.reorder(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cannedResponseKeys.lists() });
    },
  });
};
